import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/onboarding'

export const onboardingTutorial: Tutorial = {
  slug: 'onboarding',
  title: 'First-Run Onboarding',
  subtitle: 'From install to a personalized first Coach conversation',
  date: '2026-07-09',
  category: 'Account',
  platform: 'mobile',
  tags: ['onboarding', 'intent', 'notifications', 'wearables'],
  description:
    'Four short screens shown once that get a new user from "just installed" to a personalized first Coach conversation — not a generic feature tour.',
  highlights: [
    'The promise screen states the thesis before asking for anything.',
    'Intent picker seeds the first Coach conversation with a real, relevant prompt.',
    'Notifications and wearables each explain the payoff before requesting permission — both skippable.',
  ],
  coverImage: `${IMG}/01_promise.png`,
  sections: [
    {
      heading: 'The promise',
      paragraphs: [
        'The first screen states the app\'s whole thesis before asking for anything: generic health advice ignores what makes you you; Livewello\'s Coach reads your genetics, labs, and records together instead of making you re-explain your history every time.',
      ],
      image: {
        src: `${IMG}/01_promise.png`,
        alt: 'Onboarding promise slide explaining personalized Coach',
        caption: 'The promise slide',
      },
    },
    {
      heading: 'What should your plan help with first?',
      paragraphs: [
        'Rather than dropping the user straight into an empty chat box, onboarding asks a single, low-friction question: managing a health condition, caring for someone else, understanding genetics, or building a habit.',
        'This choice seeds the Wellness Coach\'s first conversation with a real prompt tailored to that intent, so the very first exchange is relevant instead of generic.',
      ],
      image: {
        src: `${IMG}/02_intent_picker.png`,
        alt: 'Onboarding intent picker with four options',
        caption: 'Intent picker',
      },
    },
    {
      heading: 'Notifications',
      paragraphs: [
        'A plain-language case for enabling push — reminders for goals, sharing invites, and check-in nudges — with an explicit **Not now** escape hatch, not just a system permission dialog with no context.',
      ],
      image: {
        src: `${IMG}/03_notifications.png`,
        alt: 'Onboarding notifications permission screen',
        caption: 'Notifications',
      },
    },
    {
      heading: 'Wearables',
      paragraphs: [
        'Same pattern for health-data connection: explain the payoff (the Coach can factor in steps, sleep, heart rate, and workouts, and can auto-check-in on step-count goals) before asking for the HealthKit/Health Connect permission. Both permission screens can be skipped — nothing in onboarding is a hard gate.',
      ],
      image: {
        src: `${IMG}/04_wearables.png`,
        alt: 'Onboarding wearables connection screen',
        caption: 'Wearables',
      },
    },
  ],
  whyMatters:
    'Every step answers "why should I do this?" before asking for anything — a picked intent shapes the first real conversation, a notification ask explains what it\'s for, a wearables ask explains what it unlocks. That\'s the difference between onboarding that feels like a tutorial and onboarding that feels like the product already understands what the user showed up for.',
  relatedSlugs: ['wellness-coach', 'multi-profile'],
  meta: {
    description:
      'Livewello mobile onboarding: four screens from install to a personalized first Wellness Coach conversation.',
    ogImage: `${IMG}/01_promise.png`,
  },
}
